import { ExternalLink, Search, Clock, Calendar, Plane, FileText, Timer } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { LocalizedLink } from "@/components/LocalizedLink";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CTASection from "@/components/CTASection";
import { useLanguage } from "@/hooks/useLanguage";

const content = {
  en: {
    hero: {
      title: "Immigration Tools & Resources",
      subtitle: "Quick access to important immigration tools and government resources"
    },
    tools: [
      { title: "USCIS Case Status", description: "Check the status of your USCIS case using your receipt number" },
      { title: "USCIS Processing Times", description: "View current processing times for all USCIS forms and service centers" },
      { title: "Visa Bulletin", description: "Check visa availability and priority dates for family and employment-based immigration" },
      { title: "I-94 (Arrival/Departure Record)", description: "Retrieve your I-94 admission record and travel history" },
      { title: "Visa Appointment Wait Times", description: "Check visa interview appointment wait times at U.S. embassies and consulates worldwide" },
      { title: "Immigration Forms (USCIS)", description: "Download official USCIS immigration forms and instructions" },
    ],
    visitSite: "Visit Site",
    helpSection: {
      title: "Need Help Navigating These Tools?",
      description: "These government websites can be confusing and difficult to navigate. If you need assistance understanding your case status, processing times, or any immigration matter, our experienced attorneys are here to help.",
      button: "Schedule Consultation"
    },
    cta: {
      title: "Let Us Handle the Paperwork",
      description: "Immigration forms and procedures can be overwhelming. Our experienced team can guide you through every step of your immigration journey."
    }
  },
  es: {
    hero: {
      title: "Herramientas y Recursos de Inmigración",
      subtitle: "Acceso rápido a herramientas de inmigración y recursos gubernamentales importantes"
    },
    tools: [
      { title: "Estado de Caso USCIS", description: "Verifique el estado de su caso de USCIS usando su número de recibo" },
      { title: "Tiempos de Procesamiento USCIS", description: "Vea los tiempos de procesamiento actuales para todos los formularios y centros de servicio de USCIS" },
      { title: "Boletín de Visas", description: "Verifique la disponibilidad de visas y fechas de prioridad para inmigración familiar y laboral" },
      { title: "I-94 (Registro de Entrada/Salida)", description: "Obtenga su registro de admisión I-94 e historial de viajes" },
      { title: "Tiempos de Espera para Citas de Visa", description: "Verifique los tiempos de espera para entrevistas de visa en embajadas y consulados de EE.UU." },
      { title: "Formularios de Inmigración (USCIS)", description: "Descargue formularios oficiales de inmigración de USCIS e instrucciones" },
    ],
    visitSite: "Visitar Sitio",
    helpSection: {
      title: "¿Necesita Ayuda con Estas Herramientas?",
      description: "Estos sitios web del gobierno pueden ser confusos y difíciles de navegar. Si necesita ayuda para entender el estado de su caso, tiempos de procesamiento, o cualquier asunto de inmigración, nuestros abogados experimentados están aquí para ayudarle.",
      button: "Programar Consulta"
    },
    cta: {
      title: "Déjenos Manejar el Papeleo",
      description: "Los formularios y procedimientos de inmigración pueden ser abrumadores. Nuestro equipo experimentado puede guiarle en cada paso de su proceso de inmigración."
    }
  },
  vi: {
    hero: {
      title: "Công Cụ & Tài Nguyên Di Trú",
      subtitle: "Truy cập nhanh các công cụ di trú quan trọng và tài nguyên chính phủ"
    },
    tools: [
      { title: "Trạng Thái Hồ Sơ USCIS", description: "Kiểm tra trạng thái hồ sơ USCIS của bạn bằng số biên nhận" },
      { title: "Thời Gian Xử Lý USCIS", description: "Xem thời gian xử lý hiện tại cho tất cả mẫu đơn và trung tâm dịch vụ USCIS" },
      { title: "Bản Tin Visa", description: "Kiểm tra tình trạng visa và ngày ưu tiên cho di trú gia đình và lao động" },
      { title: "I-94 (Hồ Sơ Nhập/Xuất Cảnh)", description: "Truy xuất hồ sơ nhập cảnh I-94 và lịch sử du lịch của bạn" },
      { title: "Thời Gian Chờ Hẹn Visa", description: "Kiểm tra thời gian chờ phỏng vấn visa tại các đại sứ quán và lãnh sự quán Hoa Kỳ" },
      { title: "Mẫu Đơn Di Trú (USCIS)", description: "Tải xuống mẫu đơn di trú chính thức của USCIS và hướng dẫn" },
    ],
    visitSite: "Truy Cập",
    helpSection: {
      title: "Cần Trợ Giúp Sử Dụng Các Công Cụ Này?",
      description: "Các trang web chính phủ này có thể khó hiểu và khó điều hướng. Nếu bạn cần hỗ trợ hiểu trạng thái hồ sơ, thời gian xử lý, hoặc bất kỳ vấn đề di trú nào, các luật sư giàu kinh nghiệm của chúng tôi sẵn sàng giúp đỡ.",
      button: "Đặt Lịch Tư Vấn"
    },
    cta: {
      title: "Để Chúng Tôi Xử Lý Giấy Tờ",
      description: "Các mẫu đơn và thủ tục di trú có thể rất phức tạp. Đội ngũ giàu kinh nghiệm của chúng tôi có thể hướng dẫn bạn qua từng bước trong hành trình di trú."
    }
  }
};

const toolUrls = [
  "https://egov.uscis.gov/casestatus/landing.do",
  "https://egov.uscis.gov/processing-times/",
  "https://travel.state.gov/content/travel/en/legal/visa-law0/visa-bulletin.html",
  "https://i94.cbp.dhs.gov/",
  "https://travel.state.gov/content/travel/en/us-visas/visa-information-resources/wait-times.html",
  "https://www.uscis.gov/forms",
];

const toolIcons = [Search, Clock, Calendar, Plane, Timer, FileText];

const Tools = () => {
  const { language } = useLanguage();
  const t = content[language];

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-hero text-primary-foreground">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">{t.hero.title}</h1>
            <p className="text-xl opacity-95">{t.hero.subtitle}</p>
          </div>
        </div>
      </section>

      {/* Tools Grid */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.tools.map((tool, index) => {
              const Icon = toolIcons[index];
              return (
                <Card key={index} className="hover:shadow-xl transition-all hover:-translate-y-1 border-t-4 border-t-accent">
                  <CardHeader>
                    <div className="h-14 w-14 rounded-xl bg-gradient-gold-shine flex items-center justify-center mb-4 shadow-gold">
                      <Icon className="h-7 w-7 text-primary" />
                    </div>
                    <CardTitle className="text-xl">{tool.title}</CardTitle>
                    <CardDescription>{tool.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Button asChild variant="default" className="w-full">
                      <a href={toolUrls[index]} target="_blank" rel="noopener noreferrer">
                        {t.visitSite} <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <div className="mt-12 max-w-4xl mx-auto bg-card p-8 rounded-lg shadow-md">
            <h2 className="text-2xl font-bold mb-4">{t.helpSection.title}</h2>
            <p className="text-muted-foreground mb-6">{t.helpSection.description}</p>
            <Button asChild variant="cta" size="lg">
              <LocalizedLink to="/booking">{t.helpSection.button}</LocalizedLink>
            </Button>
          </div>
        </div>
      </section>

      <CTASection title={t.cta.title} description={t.cta.description} />

      <Footer />
    </div>
  );
};

export default Tools;
